import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  fetchTickets,
  fetchTicketStats,
  fetchTicketDetail,
  exportTickets,
  syncTicket,
  customerConfirm,
  customerReturn,
  assignTask,
} from '@/services/api/tickets'

const TICKET_REFRESH_INTERVAL_MS = 30_000

export function useTicketList(params: {
  page?: number
  page_size?: number
  platform_status?: string
  platform_status_group?: string
  assignee?: string
  tech_owner?: string
  keyword?: string
  person?: string
  created_from?: string
  created_to?: string
  sort_by?: string
  sort_order?: string
}) {
  return useQuery({
    queryKey: ['tickets', 'list', params],
    queryFn: () => fetchTickets(params),
    staleTime: 15_000,
    refetchInterval: TICKET_REFRESH_INTERVAL_MS,
  })
}

export function useTicketStats(params: {
  platform_status?: string
  platform_status_group?: string
  assignee?: string
  tech_owner?: string
  keyword?: string
  person?: string
  created_from?: string
  created_to?: string
}) {
  return useQuery({
    queryKey: ['tickets', 'stats', params],
    queryFn: () => fetchTicketStats(params),
    staleTime: 15_000,
    refetchInterval: TICKET_REFRESH_INTERVAL_MS,
  })
}

export function useExportTickets() {
  return useMutation({
    mutationFn: exportTickets,
  })
}

export function useTicketDetail(mappingId: string | null) {
  return useQuery({
    queryKey: ['tickets', 'detail', mappingId],
    queryFn: () => fetchTicketDetail(mappingId!),
    enabled: !!mappingId,
    refetchInterval: TICKET_REFRESH_INTERVAL_MS,
  })
}

export function useSyncTicket() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (taskId: number) => syncTicket(taskId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tickets'] })
    },
  })
}

export function useCustomerConfirm() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ mappingId, note, files }: { mappingId: string; note: string; files?: File[] }) =>
      customerConfirm(mappingId, note, files),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tickets'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useCustomerReturn() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ mappingId, reason }: { mappingId: string; reason: string }) =>
      customerReturn(mappingId, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tickets'] })
    },
  })
}

/** 手动改派禅道 task */
export function useAssignTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ mappingId, assignee }: { mappingId: string; assignee: string }) =>
      assignTask(mappingId, assignee),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tickets'] })
    },
  })
}
